import type { z } from "zod";

import instance from "..";
import { looseArray, looseParse } from "../looseParse";
import {
  type currencyCloudBeneficiarySchema,
  currencyCloudTransferSchema,
  getCurrencyCloudAccountResponseSchema,
  getCurrencyCloudBeneficiaryResponseSchema,
  getCurrencyCloudStatusResponseSchema,
} from "./currencyCloud.schema";

export type GetCurrencyCloudAccountResponse = z.infer<
  typeof getCurrencyCloudAccountResponseSchema
>;

export const getCurrencyCloudAccount = async (
  accountId: string,
): Promise<GetCurrencyCloudAccountResponse> => {
  const res = await instance.get<GetCurrencyCloudAccountResponse>(
    `/currency_cloud/${accountId}/funding_account`,
  );
  return looseParse(
    getCurrencyCloudAccountResponseSchema,
    res.data,
    "getCurrencyCloudAccount",
  );
};

export const createCurrencyCloudAccount = async (accountId: string) => {
  await instance.post(`/currency_cloud/${accountId}/funding_account`);
};

export type GetCurrencyCloudStatusResponse = z.infer<
  typeof getCurrencyCloudStatusResponseSchema
>;

export const getCurrencyCloudStatus = async (
  accountId: string,
): Promise<GetCurrencyCloudStatusResponse> => {
  const res = await instance.get<GetCurrencyCloudStatusResponse>(
    `/currency_cloud/${accountId}/status`,
  );
  return looseParse(
    getCurrencyCloudStatusResponseSchema,
    res.data,
    "getCurrencyCloudStatus",
  );
};

export type CurrencyCloudTransfer = z.infer<typeof currencyCloudTransferSchema>;

export type GetCurrencyCloudTransfersResponse = CurrencyCloudTransfer[];

export const getCurrencyCloudTransfers = async (
  accountId: string,
): Promise<GetCurrencyCloudTransfersResponse> => {
  const res = await instance.get<GetCurrencyCloudTransfersResponse>(
    `/currency_cloud/${accountId}/transfers`,
  );
  return looseParse(
    looseArray(currencyCloudTransferSchema, "getCurrencyCloudTransfers"),
    res.data,
    "getCurrencyCloudTransfers",
  );
};

export type CreateCurrencyCloudTransferRequest = {
  amount: string;
};

export const createCurrencyCloudTransfer = async (
  accountId: string,
  data: CreateCurrencyCloudTransferRequest,
) => {
  const res = await instance.post(
    `/currency_cloud/${accountId}/transfers`,
    data,
  );
  return res.data;
};

export type CurrencyCloudBeneficiary = z.infer<
  typeof currencyCloudBeneficiarySchema
>;

export type GetCurrencyCloudBeneficiaryResponse = z.infer<
  typeof getCurrencyCloudBeneficiaryResponseSchema
>;

export const getCurrencyCloudBeneficiaryRequiredDetails = async (
  accountId: string,
) => {
  const res = await instance.get<GetCurrencyCloudBeneficiaryResponse>(
    `/currency_cloud/${accountId}/beneficiary_required_details`,
  );
  return looseParse(
    getCurrencyCloudBeneficiaryResponseSchema,
    res.data,
    "getCurrencyCloudBeneficiaryRequiredDetails",
  );
};

export type CreateCurrencyCloudBeneficiaryRequest = CurrencyCloudBeneficiary;

export const createCurrencyCloudBeneficiary = async (
  accountId: string,
  data: CreateCurrencyCloudBeneficiaryRequest,
) => {
  const res = await instance.post(
    `/currency_cloud/${accountId}/beneficiary`,
    data,
  );
  return res.data;
};
